import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import axios from "axios";

import {
  serverURL
} from "../../services/FetchNodeservices";

import toast from "react-hot-toast";


// =====================================================
// MY PAYSLIP
// =====================================================

export default function MyPayslip() {

  // =====================================================
  // CURRENT DATE
  // =====================================================

  const currentDate =
    new Date();

  const currentYear =
    currentDate.getFullYear();

  const currentMonth =
    currentDate.getMonth() + 1;


  // =====================================================
  // FILTER STATE
  // =====================================================

  const [
    selectedMonth,
    setSelectedMonth
  ] = useState("all");


  const [
    selectedYear,
    setSelectedYear
  ] = useState(
    String(currentYear)
  );


  // =====================================================
  // DOWNLOAD LOADING
  // =====================================================

  const [
    loading,
    setLoading
  ] = useState(null);


  // =====================================================
  // AVAILABILITY LOADING
  // =====================================================

  const [
    availabilityLoading,
    setAvailabilityLoading
  ] = useState(false);


  // =====================================================
  // EMPLOYEE JOINING DATE
  // =====================================================

  const [
    dateOfJoin,
    setDateOfJoin
  ] = useState(null);


  // =====================================================
  // JOINING DATE LOADING
  // =====================================================

  const [
    joiningDateLoading,
    setJoiningDateLoading
  ] = useState(true);


  // =====================================================
  // JOINING DATE ERROR
  // =====================================================

  const [
    joiningDateError,
    setJoiningDateError
  ] = useState(false);


  // =====================================================
  // PAYSLIP AVAILABILITY
  //
  // Example:
  //
  // {
  //   "8-2026": {
  //      available: true,
  //      status: "Generated"
  //   }
  // }
  //
  // =====================================================

  const [
    availability,
    setAvailability
  ] = useState({});


  // =====================================================
  // MONTHS
  // =====================================================

  const months = [
    {
      value: 1,
      label: "January"
    },
    {
      value: 2,
      label: "February"
    },
    {
      value: 3,
      label: "March"
    },
    {
      value: 4,
      label: "April"
    },
    {
      value: 5,
      label: "May"
    },
    {
      value: 6,
      label: "June"
    },
    {
      value: 7,
      label: "July"
    },
    {
      value: 8,
      label: "August"
    },
    {
      value: 9,
      label: "September"
    },
    {
      value: 10,
      label: "October"
    },
    {
      value: 11,
      label: "November"
    },
    {
      value: 12,
      label: "December"
    }
  ];


  // =====================================================
  // YEARS
  // =====================================================

  const years = Array.from(
    {
      length: 5
    },
    (_, index) =>
      currentYear - index
  );


  // =====================================================
  // GET TOKEN
  // =====================================================

  const getToken = () => {

    return localStorage.getItem(
      "TOKEN"
    );

  };


  // =====================================================
  // FETCH JOINING DATE
  //
  // HRMS:
  //
  // GET /api/my-payslip/availability
  //
  // Backend internally calls Payroll.
  //
  // No PAYROLL_API_KEY here.
  // =====================================================

  useEffect(() => {

    const fetchJoiningDate =
      async () => {

        try {

          setJoiningDateLoading(
            true
          );

          setJoiningDateError(
            false
          );


          // =================================================
          // TOKEN
          // =================================================

          const token =
            getToken();


          if (!token) {

            toast.error(
              "Your session has expired. Please login again."
            );

            setJoiningDateError(
              true
            );

            return;

          }


          // =================================================
          // CALL HRMS BACKEND
          // =================================================

          const response =
            await axios.get(
              `${serverURL}/api/my-payslip/availability`,
              {

                headers: {
                  Authorization:
                    `Bearer ${token}`
                },

                timeout: 30000

              }
            );


          console.log(
            "Payslip Availability:",
            response.data
          );


          // =================================================
          // CHECK SUCCESS
          // =================================================

          if (
            response.data?.success === false
          ) {

            toast.error(
              response.data?.message ||
              "Unable to load payslip information."
            );

            setJoiningDateError(
              true
            );

            return;

          }


          // =================================================
          // GET DATE OF JOIN
          // =================================================

          const joiningDate =
            response.data?.date_of_join ||
            response.data?.data?.date_of_join ||
            response.data?.employee?.date_of_join;


if (!joiningDate) {
  console.warn(
    "Date of joining not returned by backend. Continuing without joining date."
  );

  // Joining date is optional for payslip display.
  // Payroll availability will decide whether payslip exists.

  setDateOfJoin(null);
  return;
}


          // =================================================
          // SAVE DATE
          // =================================================

          setDateOfJoin(
            joiningDate
          );

        } catch (error) {

          console.error(
            "Joining Date Fetch Error:",
            error
          );


          // =================================================
          // 401
          // =================================================

          if (
            error.response?.status === 401
          ) {

            toast.error(
              "Session expired. Please login again."
            );

          } else {

            toast.error(
              error.response?.data?.message ||
              "Unable to load employee joining date."
            );

          }


          setJoiningDateError(
            true
          );

        } finally {

          setJoiningDateLoading(
            false
          );

        }

      };


    fetchJoiningDate();

  }, []);


  // =====================================================
  // PARSE JOINING DATE
  //
  // We only need YEAR and MONTH.
  //
  // Avoid:
  //
  // new Date("2026-08-15")
  //
  // because timezone conversion can create problems.
  // =====================================================

  const joiningPeriod =
    useMemo(() => {

      if (!dateOfJoin) {

        return null;

      }


      const dateString =
        String(
          dateOfJoin
        ).substring(
          0,
          10
        );


      const parts =
        dateString.split("-");


      if (
        parts.length !== 3
      ) {

        return null;

      }


      const joiningYear =
        Number(
          parts[0]
        );

      const joiningMonth =
        Number(
          parts[1]
        );


      if (
        !joiningYear ||
        !joiningMonth ||
        joiningMonth < 1 ||
        joiningMonth > 12
      ) {

        return null;

      }


      return {

        year:
          joiningYear,

        month:
          joiningMonth,

        period:
          joiningYear * 12 +
          joiningMonth

      };

    }, [
      dateOfJoin
    ]);


  // =====================================================
  // CHECK WHETHER PAYSLIP PERIOD IS VALID
  //
  // Valid means:
  //
  // 1. Not before joining month
  // 2. Not future month
  //
  // This does NOT mean payslip is generated.
  // Actual generation is checked separately.
  // =====================================================

  const isPayslipPeriodValid =
    (
      month,
      year
    ) => {

      const selectedPeriod =
        Number(year) * 12 +
        Number(month);


      // =================================================
      // FUTURE MONTH
      // =================================================

      const currentPeriod =
        currentYear * 12 +
        currentMonth;


      if (
        selectedPeriod >
        currentPeriod
      ) {

        return false;

      }


      // =================================================
      // BEFORE JOINING MONTH
      // =================================================

      if (joiningPeriod) {

        if (
          selectedPeriod <
          joiningPeriod.period
        ) {

          return false;

        }

      }


      return true;

    };


  // =====================================================
  // BUILD VALID PERIOD LIST
  //
  // This creates only periods which are:
  //
  // - After joining month
  // - Not future
  //
  // Actual availability comes later from Payroll.
  // =====================================================

  const validPeriods =
    useMemo(() => {

      const list = [];


      years.forEach(
        year => {

          months.forEach(
            month => {

              if (
                !isPayslipPeriodValid(
                  month.value,
                  year
                )
              ) {

                return;

              }


              list.push({

                month:
                  month.value,

                monthName:
                  month.label,

                year

              });

            }
          );

        }
      );


      // =================================================
      // SORT NEWEST FIRST
      // =================================================

      list.sort(
        (a, b) => {

          const periodA =
            a.year * 12 +
            a.month;

          const periodB =
            b.year * 12 +
            b.month;

          return (
            periodB -
            periodA
          );

        }
      );


      return list;

    }, [
      joiningPeriod,
      currentYear,
      currentMonth
    ]);


  // =====================================================
  // CHECK SINGLE MONTH AVAILABILITY
  //
  // HRMS calls:
  //
  // /api/my-payslip/availability
  //
  // HRMS then calls Payroll:
  //
  // /api/payslip/employee/availability
  //
  // =====================================================

  const checkPayslipAvailability =
    async (
      month,
      year,
      token
    ) => {

      try {

        const response =
          await axios.get(
            `${serverURL}/api/my-payslip/availability`,
            {

              headers: {
                Authorization:
                  `Bearer ${token}`,
                "x-month":
                  month,
                "x-year":
                  year
              },

              timeout: 30000

            }
          );


        const data =
          response.data || {};


        console.log(
          `Payslip Availability ${month}/${year}:`,
          data
        );


        return {

          available:
            Boolean(
              data.available
            ),

          status:
            data.status ||
            (
              data.available
                ? "Generated"
                : "Not Generated"
            ),

          message:
            data.message ||
            ""

        };

      } catch (error) {

        console.error(
          `Availability Error ${month}/${year}:`,
          error.response?.data ||
          error.message
        );


        // =================================================
        // SESSION EXPIRED
        // =================================================

        if (
          error.response?.status === 401
        ) {

          return {

            available:
              false,

            status:
              "Session Expired",

            message:
              "Session expired"

          };

        }


        return {

          available:
            false,

          status:
            "Not Generated",

          message:
            error.response?.data?.message ||
            "Unable to check payslip"

        };

      }

    };


  // =====================================================
  // LOAD PAYSLIP AVAILABILITY
  //
  // This runs after joining date is known.
  //
  // IMPORTANT:
  // We check actual Payroll availability instead
  // of assuming every valid month has a payslip.
  // =====================================================

  useEffect(() => {

    let cancelled = false;


    const loadAvailability =
      async () => {

        try {

          const token =
            getToken();


          if (!token) {

            return;

          }


          setAvailabilityLoading(
            true
          );


          // =================================================
          // ONLY CHECK PERIODS NOT ALREADY LOADED
          // =================================================

          const periodsToCheck =
            validPeriods.filter(
              item => {

                const key =
                  `${item.month}-${item.year}`;

                return !availability[key];

              }
            );


          if (
            periodsToCheck.length === 0
          ) {

            setAvailabilityLoading(
              false
            );

            return;

          }


          // =================================================
          // CHECK ALL PERIODS
          //
          // Promise.all allows the UI to load faster.
          // =================================================

          const results =
            await Promise.all(
              periodsToCheck.map(
                async item => {

                  const result =
                    await checkPayslipAvailability(
                      item.month,
                      item.year,
                      token
                    );


                  return {

                    key:
                      `${item.month}-${item.year}`,

                    result

                  };

                }
              )
            );


          if (cancelled) {

            return;

          }


          // =================================================
          // SAVE RESULTS
          // =================================================

          setAvailability(
            previous => {

              const updated = {
                ...previous
              };


              results.forEach(
                item => {

                  updated[
                    item.key
                  ] =
                    item.result;

                }
              );


              return updated;

            }
          );


          // =================================================
          // SESSION EXPIRED
          // =================================================

          const sessionExpired =
            results.some(
              item =>
                item.result?.status ===
                "Session Expired"
            );


          if (sessionExpired) {

            toast.error(
              "Session expired. Please login again."
            );

          }

        } catch (error) {

          console.error(
            "Payslip Availability Loading Error:",
            error
          );

        } finally {

          if (!cancelled) {

            setAvailabilityLoading(
              false
            );

          }

        }

      };


    loadAvailability();


    return () => {

      cancelled = true;

    };

  }, [
    joiningPeriod,
    validPeriods.length
  ]);


  // =====================================================
  // GET STATUS FOR A PERIOD
  // =====================================================

  const getPeriodAvailability =
    (
      month,
      year
    ) => {

      const key =
        `${month}-${year}`;


      return (
        availability[key] || {

          available:
            false,

          status:
            "Checking",

          message:
            ""

        }
      );

    };


  // =====================================================
  // DOWNLOAD PAYSLIP
  // =====================================================

  const handleDownload =
    async (
      month,
      year
    ) => {

      const loadingKey =
        `${month}-${year}`;


      try {

        // =================================================
        // FRONTEND PERIOD CHECK
        // =================================================

        if (
          !isPayslipPeriodValid(
            month,
            year
          )
        ) {

          toast.error(
            "Payslip is not available for this period."
          );

          return;

        }


        // =================================================
        // CHECK ACTUAL AVAILABILITY
        // =================================================

        const periodAvailability =
          getPeriodAvailability(
            month,
            year
          );


        if (
          !periodAvailability.available
        ) {

          if (
            periodAvailability.status ===
            "Checking"
          ) {

            toast.error(
              "Please wait while payslip availability is checked."
            );

          } else {

            toast.error(
              periodAvailability.message ||
              "Payslip is not available for this month."
            );

          }

          return;

        }


        // =================================================
        // LOADING
        // =================================================

        setLoading(
          loadingKey
        );


        // =================================================
        // TOKEN
        // =================================================

        const token =
          getToken();


        if (!token) {

          toast.error(
            "Your session has expired. Please login again."
          );

          return;

        }


        // =================================================
        // DOWNLOAD FROM HRMS
        // =================================================
const response = await axios.get(
  `${serverURL}/api/my-payslip`,
  {
    params: {
      month: Number(month),
      year: Number(year)
    },

    headers: {
      Authorization: `Bearer ${token}`
    },

    responseType: "blob",

    timeout: 120000
  }
);


        // =================================================
        // CONTENT TYPE
        // =================================================

        const contentType =
          response.headers[
            "content-type"
          ] || "";


        // =================================================
        // BACKEND MAY RETURN JSON ERROR
        // =================================================

        if (
          !contentType
            .toLowerCase()
            .includes(
              "application/pdf"
            )
        ) {

          try {

            const text =
              await response.data.text();


            const json =
              JSON.parse(text);


            toast.error(
              json.message ||
              "Payslip is not available."
            );


          } catch {

            toast.error(
              "Payslip is not available."
            );

          }


          return;

        }


        // =================================================
        // CREATE PDF BLOB
        // =================================================

        const blob =
          new Blob(
            [
              response.data
            ],
            {
              type:
                "application/pdf"
            }
          );


        // =================================================
        // CREATE DOWNLOAD URL
        // =================================================

        const downloadUrl =
          window.URL.createObjectURL(
            blob
          );


        // =================================================
        // CREATE LINK
        // =================================================

        const link =
          document.createElement(
            "a"
          );


        link.href =
          downloadUrl;


        // =================================================
        // MONTH NAME
        // =================================================

        const monthName =
          months.find(
            item =>
              item.value ===
              Number(month)
          )?.label ||
          month;


        link.download =
          `Payslip_${monthName}_${year}.pdf`;


        document.body.appendChild(
          link
        );


        link.click();


        link.remove();


        // =================================================
        // CLEAN URL
        // =================================================

        window.URL.revokeObjectURL(
          downloadUrl
        );


        toast.success(
          "Payslip downloaded successfully"
        );


      } catch (error) {

        console.error(
          "Payslip Download Error:",
          error
        );


        // =================================================
        // 401
        // =================================================

        if (
          error.response?.status ===
          401
        ) {

          toast.error(
            "Session expired. Please login again."
          );

          return;

        }


        // =================================================
        // 404
        // =================================================

        if (
          error.response?.status ===
          404
        ) {

          toast.error(
            "Payslip is not available for this month."
          );

          return;

        }


        // =================================================
        // 400
        // =================================================

        if (
          error.response?.status ===
          400
        ) {

          let message =
            "Invalid payslip request.";


          try {

            if (
              error.response.data
                instanceof Blob
            ) {

              const text =
                await error.response.data.text();


              const json =
                JSON.parse(text);


              message =
                json.message ||
                message;

            } else {

              message =
                error.response.data?.message ||
                message;

            }

          } catch {

            // Keep default message

          }


          toast.error(
            message
          );

          return;

        }


        // =================================================
        // 500
        // =================================================

        if (
          error.response?.status ===
          500
        ) {

          toast.error(
            "Payslip generation failed. Please try again."
          );

          return;

        }


        // =================================================
        // NETWORK
        // =================================================

        if (
          !error.response
        ) {

          toast.error(
            "Unable to connect to payroll service."
          );

          return;

        }


        // =================================================
        // DEFAULT
        // =================================================

        toast.error(
          "Unable to download payslip. Please try again."
        );

      } finally {

        setLoading(
          null
        );

      }

    };


  // =====================================================
  // FILTERED PAYSLIP LIST
  //
  // Actual status comes from availability state.
  // =====================================================

  const payslipList =
    useMemo(() => {

      const list =
        validPeriods.filter(
          item => {

            // =============================================
            // YEAR FILTER
            // =============================================

            if (
              selectedYear !==
                "all" &&
              item.year !==
                Number(
                  selectedYear
                )
            ) {

              return false;

            }


            // =============================================
            // MONTH FILTER
            // =============================================

            if (
              selectedMonth !==
                "all" &&
              item.month !==
                Number(
                  selectedMonth
                )
            ) {

              return false;

            }


            return true;

          }
        );


      return list.map(
        item => {

          const result =
            getPeriodAvailability(
              item.month,
              item.year
            );


          return {

            ...item,

            available:
              result.available,

            status:
              result.status,

            message:
              result.message

          };

        }
      );

    }, [
      validPeriods,
      selectedMonth,
      selectedYear,
      availability
    ]);


  // =====================================================
  // RESET FILTER
  // =====================================================

  const handleReset =
    () => {

      setSelectedMonth(
        "all"
      );

      setSelectedYear(
        String(
          currentYear
        )
      );

    };


  // =====================================================
  // FORMAT JOINING DATE
  // =====================================================

  const formattedJoiningDate =
    useMemo(() => {

      if (!dateOfJoin) {

        return "";

      }


      const dateString =
        String(
          dateOfJoin
        ).substring(
          0,
          10
        );


      const parts =
        dateString.split("-");


      if (
        parts.length !== 3
      ) {

        return dateOfJoin;

      }


      const year =
        Number(
          parts[0]
        );

      const month =
        Number(
          parts[1]
        );

      const day =
        Number(
          parts[2]
        );


      if (
        !year ||
        !month ||
        !day
      ) {

        return dateOfJoin;

      }


      return new Date(
        year,
        month - 1,
        day
      ).toLocaleDateString(
        "en-IN",
        {

          day:
            "2-digit",

          month:
            "long",

          year:
            "numeric"

        }
      );

    }, [
      dateOfJoin
    ]);


  // =====================================================
  // STATUS BADGE
  // =====================================================

  const renderStatus =
    (
      item
    ) => {

      // =================================================
      // CHECKING
      // =================================================

      if (
        item.status ===
        "Checking"
      ) {

        return (

          <span
            className="
              inline-flex
              items-center
              rounded-full
              bg-yellow-500/10
              px-3
              py-1
              text-xs
              font-medium
              text-yellow-400
            "
          >

            <span
              className="
                mr-2
                h-1.5
                w-1.5
                animate-pulse
                rounded-full
                bg-yellow-400
              "
            />

            Checking...

          </span>

        );

      }


      // =================================================
      // GENERATED
      // =================================================

      if (
        item.available
      ) {

        return (

          <span
            className="
              inline-flex
              items-center
              rounded-full
              bg-green-500/10
              px-3
              py-1
              text-xs
              font-medium
              text-green-400
            "
          >

            <span
              className="
                mr-2
                h-1.5
                w-1.5
                rounded-full
                bg-green-400
              "
            />

            Generated

          </span>

        );

      }


      // =================================================
      // NOT GENERATED
      // =================================================

      return (

        <span
          className="
            inline-flex
            items-center
            rounded-full
            bg-gray-500/10
            px-3
            py-1
            text-xs
            font-medium
            text-gray-400
          "
        >

          <span
            className="
              mr-2
              h-1.5
              w-1.5
              rounded-full
              bg-gray-500
            "
          />

          Not Generated

        </span>

      );

    };


  // =====================================================
  // UI
  // =====================================================

  return (

    <div className="p-6">


      {/* =================================================
          HEADER
      ================================================= */}

      <div className="mb-6">

        <h1
          className="
            text-2xl
            font-bold
            text-white
          "
        >
          My Payslips
        </h1>


        <p
          className="
            mt-1
            text-sm
            text-gray-400
          "
        >
          View and download your monthly salary payslips
        </p>

      </div>


      {/* =================================================
          JOINING DATE INFORMATION
      ================================================= */}

      {!joiningDateLoading &&
        !joiningDateError &&
        formattedJoiningDate && (

          <div
            className="
              mb-5
              rounded-xl
              border
              border-indigo-500/20
              bg-indigo-500/5
              px-5
              py-4
            "
          >

            <div
              className="
                flex
                flex-col
                gap-1
                sm:flex-row
                sm:items-center
                sm:justify-between
              "
            >

              <div>

                <p
                  className="
                    text-xs
                    font-semibold
                    uppercase
                    tracking-wide
                    text-gray-400
                  "
                >
                  Date of Joining
                </p>


                <p
                  className="
                    mt-1
                    text-sm
                    font-semibold
                    text-white
                  "
                >
                  {formattedJoiningDate}
                </p>

              </div>


              <div
                className="
                  text-xs
                  text-gray-500
                "
              >
                Payslips are available from the
                joining month.
              </div>

            </div>

          </div>

        )}


      {/* =================================================
          FILTER CARD
      ================================================= */}

      <div
        className="
          mb-5
          rounded-2xl
          border
          border-gray-700
          bg-[#111827]
          p-5
          shadow-lg
        "
      >

        <div
          className="
            flex
            flex-col
            gap-4
            md:flex-row
            md:items-end
          "
        >


          {/* =================================================
              MONTH FILTER
          ================================================= */}

          <div
            className="
              w-full
              md:w-64
            "
          >

            <label
              className="
                mb-2
                block
                text-xs
                font-semibold
                uppercase
                tracking-wide
                text-gray-400
              "
            >
              Month
            </label>


            <select
              value={
                selectedMonth
              }
              onChange={
                e =>
                  setSelectedMonth(
                    e.target.value
                  )
              }
              disabled={
                joiningDateLoading ||
                joiningDateError
              }
              className="
                w-full
                rounded-lg
                border
                border-gray-700
                bg-[#151d2e]
                px-4
                py-3
                text-sm
                text-white
                outline-none
                focus:border-indigo-500
                focus:ring-2
                focus:ring-indigo-500/20
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >

              <option value="all">
                All Months
              </option>


              {months.map(
                item => {

                  const isBeforeJoining =
                    joiningPeriod &&
                    selectedYear !== "all" &&
                    (
                      Number(
                        selectedYear
                      ) <
                      joiningPeriod.year ||

                      (
                        Number(
                          selectedYear
                        ) ===
                        joiningPeriod.year &&

                        item.value <
                        joiningPeriod.month
                      )
                    );


                  return (

                    <option
                      key={
                        item.value
                      }
                      value={
                        item.value
                      }
                      disabled={
                        Boolean(
                          isBeforeJoining
                        )
                      }
                    >
                      {item.label}
                    </option>

                  );

                }
              )}

            </select>

          </div>


          {/* =================================================
              YEAR FILTER
          ================================================= */}

          <div
            className="
              w-full
              md:w-64
            "
          >

            <label
              className="
                mb-2
                block
                text-xs
                font-semibold
                uppercase
                tracking-wide
                text-gray-400
              "
            >
              Year
            </label>


            <select
              value={
                selectedYear
              }
              onChange={
                e =>
                  setSelectedYear(
                    e.target.value
                  )
              }
              disabled={
                joiningDateLoading ||
                joiningDateError
              }
              className="
                w-full
                rounded-lg
                border
                border-gray-700
                bg-[#151d2e]
                px-4
                py-3
                text-sm
                text-white
                outline-none
                focus:border-indigo-500
                focus:ring-2
                focus:ring-indigo-500/20
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >

              <option value="all">
                All Years
              </option>


              {years.map(
                year => (

                  <option
                    key={
                      year
                    }
                    value={
                      year
                    }
                  >
                    {year}
                  </option>

                )
              )}

            </select>

          </div>


          {/* =================================================
              RESET
          ================================================= */}

          <button
            type="button"
            onClick={
              handleReset
            }
            disabled={
              joiningDateLoading
            }
            className="
              rounded-lg
              border
              border-gray-700
              bg-[#151d2e]
              px-5
              py-3
              text-sm
              font-semibold
              text-gray-300
              transition
              hover:bg-gray-800
              hover:text-white
              disabled:cursor-not-allowed
              disabled:opacity-50
            "
          >
            Reset
          </button>


          {/* =================================================
              AVAILABILITY STATUS
          ================================================= */}

          {availabilityLoading && (

            <div
              className="
                flex
                items-center
                gap-2
                pb-1
                text-xs
                text-gray-500
              "
            >

              <span
                className="
                  h-3
                  w-3
                  animate-spin
                  rounded-full
                  border-2
                  border-indigo-500
                  border-t-transparent
                "
              />

              Checking payslips...

            </div>

          )}

        </div>

      </div>


      {/* =================================================
          JOINING DATE LOADING
      ================================================= */}

      {joiningDateLoading && (

        <div
          className="
            mb-5
            rounded-2xl
            border
            border-gray-700
            bg-[#111827]
            px-6
            py-10
            text-center
          "
        >

          <div
            className="
              mx-auto
              mb-3
              h-8
              w-8
              animate-spin
              rounded-full
              border-2
              border-indigo-500
              border-t-transparent
            "
          />


          <p
            className="
              text-sm
              text-gray-400
            "
          >
            Loading payslip information...
          </p>

        </div>

      )}


      {/* =================================================
          JOINING DATE ERROR
      ================================================= */}

      {!joiningDateLoading &&
        joiningDateError && (

          <div
            className="
              mb-5
              rounded-2xl
              border
              border-red-500/20
              bg-red-500/5
              px-6
              py-10
              text-center
            "
          >

            <div
              className="
                mb-3
                text-4xl
              "
            >
              ⚠️
            </div>


            <h3
              className="
                text-sm
                font-semibold
                text-white
              "
            >
              Unable to load payslip information
            </h3>


            <p
              className="
                mt-1
                text-xs
                text-gray-500
              "
            >
              Please refresh the page or contact HR.
            </p>

          </div>

        )}


      {/* =================================================
          PAYSLIP LIST
      ================================================= */}

      {!joiningDateLoading &&
        !joiningDateError && (

          <div
            className="
              overflow-hidden
              rounded-2xl
              border
              border-gray-700
              bg-[#111827]
              shadow-lg
            "
          >


            {/* =================================================
                TABLE HEADER
            ================================================= */}

            <div
              className="
                grid
                grid-cols-12
                border-b
                border-gray-700
                bg-[#151d2e]
                px-6
                py-4
                text-xs
                font-semibold
                uppercase
                tracking-wide
                text-gray-400
              "
            >

              <div
                className="
                  col-span-5
                "
              >
                Payslip Period
              </div>


              <div
                className="
                  col-span-3
                "
              >
                Year
              </div>


              <div
                className="
                  col-span-2
                "
              >
                Status
              </div>


              <div
                className="
                  col-span-2
                  text-right
                "
              >
                Action
              </div>

            </div>


            {/* =================================================
                LIST
            ================================================= */}

            {payslipList.length > 0 ? (

              payslipList.map(
                item => {

                  const loadingKey =
                    `${item.month}-${item.year}`;


                  const isLoading =
                    loading ===
                    loadingKey;


                  const isAvailable =
                    item.available;


                  const isChecking =
                    item.status ===
                    "Checking";


                  return (

                    <div
                      key={
                        loadingKey
                      }
                      className="
                        grid
                        grid-cols-12
                        items-center
                        border-b
                        border-gray-800
                        px-6
                        py-4
                        transition
                        hover:bg-[#172033]
                      "
                    >


                      {/* =================================================
                          PERIOD
                      ================================================= */}

                      <div
                        className="
                          col-span-5
                        "
                      >

                        <div
                          className="
                            flex
                            items-center
                            gap-3
                          "
                        >

                          <div
                            className="
                              flex
                              h-10
                              w-10
                              items-center
                              justify-center
                              rounded-lg
                              bg-indigo-500/10
                              text-lg
                            "
                          >
                            📄
                          </div>


                          <div>

                            <p
                              className="
                                text-sm
                                font-semibold
                                text-white
                              "
                            >
                              {
                                item.monthName
                              }{" "}
                              {
                                item.year
                              }
                            </p>


                            <p
                              className="
                                text-xs
                                text-gray-500
                              "
                            >
                              Monthly Payslip
                            </p>

                          </div>

                        </div>

                      </div>


                      {/* =================================================
                          YEAR
                      ================================================= */}

                      <div
                        className="
                          col-span-3
                          text-sm
                          text-gray-300
                        "
                      >
                        {
                          item.year
                        }
                      </div>


                      {/* =================================================
                          STATUS
                      ================================================= */}

                      <div
                        className="
                          col-span-2
                        "
                      >

                        {renderStatus(
                          item
                        )}

                      </div>


                      {/* =================================================
                          ACTION
                      ================================================= */}

                      <div
                        className="
                          col-span-2
                          flex
                          justify-end
                        "
                      >

                        <button
                          type="button"
                          onClick={() =>
                            handleDownload(
                              item.month,
                              item.year
                            )
                          }
                          disabled={
                            isLoading ||
                            !isAvailable ||
                            isChecking
                          }
                          className="
                            inline-flex
                            items-center
                            gap-2
                            rounded-lg
                            bg-indigo-600
                            px-4
                            py-2
                            text-xs
                            font-semibold
                            text-white
                            transition
                            hover:bg-indigo-700
                            disabled:cursor-not-allowed
                            disabled:opacity-40
                          "
                        >

                          {isLoading ? (

                            <>

                              <span
                                className="
                                  h-3.5
                                  w-3.5
                                  animate-spin
                                  rounded-full
                                  border-2
                                  border-white
                                  border-t-transparent
                                "
                              />

                              Generating...

                            </>

                          ) : isChecking ? (

                            <>

                              <span
                                className="
                                  h-3.5
                                  w-3.5
                                  animate-spin
                                  rounded-full
                                  border-2
                                  border-white
                                  border-t-transparent
                                "
                              />

                              Checking...

                            </>

                          ) : isAvailable ? (

                            <>

                              <span>
                                ↓
                              </span>

                              Download

                            </>

                          ) : (

                            <>

                              <span>
                                —
                              </span>

                              Unavailable

                            </>

                          )}

                        </button>

                      </div>

                    </div>

                  );

                }
              )

            ) : (

              // =================================================
              // NO RESULT
              // =================================================

              <div
                className="
                  px-6
                  py-16
                  text-center
                "
              >

                <div
                  className="
                    mb-3
                    text-4xl
                  "
                >
                  📄
                </div>


                <h3
                  className="
                    text-sm
                    font-semibold
                    text-white
                  "
                >
                  No payslips found
                </h3>


                <p
                  className="
                    mt-1
                    text-xs
                    text-gray-500
                  "
                >
                  No payslips are available for the selected period.
                </p>

              </div>

            )}

          </div>

        )}

    </div>

  );

}